import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OrgProvider } from './contexts/OrgContext';
import { SelectionProvider } from './contexts/SelectionContext';
import { ViewSyncProvider } from './contexts/ViewSyncContext';
import { ToastProvider } from './components/Toast';
import DemoModeToaster from './components/DemoModeToaster';
import UpdateToastHost from './components/UpdateToastHost';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Datasets from './pages/Datasets';
import DatasetDetail from './pages/DatasetDetail';
import Upload from './pages/Upload';
import MapView from './pages/MapView';
import Viewer3D from './pages/Viewer3D';
import Settings from './pages/Settings';
import Help from './pages/Help';
import GlobeLaunch from './pages/GlobeLaunch';
import { onGeostack, portalSignInUrl, PORTAL_URL } from './lib/portal';

/** Leave the app for another page on geostack.com (the portal). */
function LeaveFor({ url }) {
  useEffect(() => { window.location.replace(url); }, [url]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading, signingOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  if (user) return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
  // On geostack.com there is no sign-in of our own: the portal signs people in and sends them
  // back here (`next` is honoured because /gis/ is in the portal's app list). Signing out on
  // purpose goes to the portal's front door instead, not round-trip back into this app.
  if (onGeostack()) return <LeaveFor url={signingOut ? PORTAL_URL : portalSignInUrl()} />;
  return <Navigate to="/signin" />;
}

/** Our own sign-in pages, for localhost and the raw Pages host only. */
function LocalAuthRoute({ children }) {
  return onGeostack() ? <LeaveFor url={PORTAL_URL} /> : children;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <OrgProvider>
      <SelectionProvider>
      <ViewSyncProvider>
      <ToastProvider>
        <UpdateToastHost />
        <DemoModeToaster />
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <Routes>
            <Route path="/signin" element={<LocalAuthRoute><SignIn /></LocalAuthRoute>} />
            <Route path="/signup" element={<LocalAuthRoute><SignUp /></LocalAuthRoute>} />
            <Route path="/forgot-password" element={<LocalAuthRoute><ForgotPassword /></LocalAuthRoute>} />
            <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/datasets"
            element={
              <ProtectedRoute>
                <Datasets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/datasets/:id"
            element={
              <ProtectedRoute>
                <DatasetDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <Upload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/map"
            element={
              <ProtectedRoute>
                <MapView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/3d"
            element={
              <ProtectedRoute>
                <Viewer3D />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/globe-launch"
            element={
              <ProtectedRoute>
                <GlobeLaunch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
    </ViewSyncProvider>
    </SelectionProvider>
    </OrgProvider>
  </AuthProvider>
  </ThemeProvider>
  );
}

export default App;
